import {
  EMPTY_IMPORT_MAPPING,
  IMPORT_TARGET_FIELDS,
  REQUIRED_IMPORT_TARGET_FIELDS,
  type ImportCommitRequest,
  type ImportCommitResponse,
  type ImportMatchBasis,
  type ImportMatchConfidence,
  type ImportMapping,
  type ImportParseResponse,
  type ImportPreviewRow,
  type ImportReviewFilter,
  type ImportTargetField,
  type ImportValidateResponse,
} from "./externalRegisterImportTypes";
import i18n from "@/i18n";

export const PAGE_SIZE = 50;
export const IMPORT_COMMIT_BATCH_SIZE = 50;
export const IMPORT_COMMIT_DELAY_MIN_MS = 3000;
export const IMPORT_COMMIT_DELAY_MAX_MS = 8000;
export const IMPORT_COMMIT_SIMULATION_ROW_COUNT = 2000;

export type ImportStage = "source" | "parse" | "mapping" | "review" | "report";
export type ImportStageStatus = "complete" | "inProgress" | "waiting";
type ImportCommitRowOutcome = ImportCommitResponse["rows"][number];
type ImportCommitBatchOutcome = ImportCommitResponse["batches"][number];

const COMMITTED_IMPORT_STATUSES = new Set<ImportCommitRowOutcome["status"]>([
  "MATCHED_UMRN",
  "MATCHED_DEMOGRAPHICS",
  "CREATED_NEW",
  "UPDATED_EXISTING",
]);

export const IMPORT_STAGES: Array<{ value: ImportStage; label: string }> = [
  { value: "source", label: i18n.t("externalRegister.import.stages.labels.source", { defaultValue: "Source" }) },
  { value: "parse", label: i18n.t("externalRegister.import.stages.labels.parse", { defaultValue: "Parse" }) },
  { value: "mapping", label: i18n.t("externalRegister.import.stages.labels.mapping", { defaultValue: "Map" }) },
  { value: "review", label: i18n.t("externalRegister.import.stages.labels.review", { defaultValue: "Review" }) },
  { value: "report", label: i18n.t("externalRegister.import.stages.labels.report", { defaultValue: "Report" }) },
];

export const REVIEW_FILTERS: Array<{ value: ImportReviewFilter; label: string }> = [
  { value: "all", label: i18n.t("externalRegister.import.review.filters.all", { defaultValue: "All" }) },
  { value: "needsReview", label: i18n.t("externalRegister.import.review.filters.needsReview", { defaultValue: "Needs review" }) },
  { value: "ready", label: i18n.t("externalRegister.import.review.filters.ready", { defaultValue: "Ready to import" }) },
  { value: "errors", label: i18n.t("externalRegister.import.review.filters.errors", { defaultValue: "Errors" }) },
  { value: "matched", label: i18n.t("externalRegister.import.review.filters.matched", { defaultValue: "Already matched/updates" }) },
  { value: "createsNew", label: i18n.t("externalRegister.import.review.filters.createsNew", { defaultValue: "Creates new patient" }) },
  { value: "matchUmrn", label: i18n.t("externalRegister.import.review.filters.matchUmrn", { defaultValue: "UMRN" }) },
  { value: "matchNameDobSex", label: i18n.t("externalRegister.import.review.filters.matchNameDobSex", { defaultValue: "Name+DOB+sex" }) },
  { value: "matchNameDob", label: i18n.t("externalRegister.import.review.filters.matchNameDob", { defaultValue: "Name+DOB" }) },
  { value: "matchNameSex", label: i18n.t("externalRegister.import.review.filters.matchNameSex", { defaultValue: "Name+sex" }) },
  { value: "matchNameOnly", label: i18n.t("externalRegister.import.review.filters.matchNameOnly", { defaultValue: "Name only" }) },
];

export type ImportApprovalThreshold = Exclude<ImportMatchConfidence, "ERROR">;
export type ImportApprovalBasis = "allMatched" | Exclude<ImportMatchBasis, "ERROR">;

export const IMPORT_APPROVAL_THRESHOLDS: Array<{ value: ImportApprovalThreshold; label: string }> = [
  { value: "HIGH", label: i18n.t("externalRegister.import.review.bulk.thresholds.HIGH", { defaultValue: "HIGH only" }) },
  { value: "MEDIUM", label: i18n.t("externalRegister.import.review.bulk.thresholds.MEDIUM", { defaultValue: "MEDIUM and above" }) },
  { value: "LOW", label: i18n.t("externalRegister.import.review.bulk.thresholds.LOW", { defaultValue: "LOW and above" }) },
];

export const IMPORT_APPROVAL_BASIS_OPTIONS: Array<{ value: ImportApprovalBasis; label: string }> = [
  { value: "allMatched", label: i18n.t("externalRegister.import.review.bulk.basis.allMatched", { defaultValue: "All matched rows" }) },
  { value: "UMRN", label: i18n.t("externalRegister.import.review.bulk.basis.UMRN", { defaultValue: "UMRN" }) },
  { value: "NAME_DOB_SEX", label: i18n.t("externalRegister.import.review.bulk.basis.NAME_DOB_SEX", { defaultValue: "Name+DOB+sex" }) },
  { value: "NAME_DOB", label: i18n.t("externalRegister.import.review.bulk.basis.NAME_DOB", { defaultValue: "Name+DOB" }) },
  { value: "NAME_SEX", label: i18n.t("externalRegister.import.review.bulk.basis.NAME_SEX", { defaultValue: "Name+sex" }) },
  { value: "NAME_ONLY", label: i18n.t("externalRegister.import.review.bulk.basis.NAME_ONLY", { defaultValue: "Name only" }) },
  { value: "CREATED_NEW", label: i18n.t("externalRegister.import.review.bulk.basis.CREATED_NEW", { defaultValue: "Creates new patient" }) },
];

const CONFIDENCE_RANK: Record<ImportMatchConfidence, number> = {
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
  ERROR: 0,
};

export const getRandomCommitBatchDelayMs = (random = Math.random): number => {
  const boundedRandom = Math.min(0.999999, Math.max(0, random()));
  return Math.floor(IMPORT_COMMIT_DELAY_MIN_MS + boundedRandom * (IMPORT_COMMIT_DELAY_MAX_MS - IMPORT_COMMIT_DELAY_MIN_MS + 1));
};

export const getCommitBatchDelayMs = (configuredDelayMs: number | null | undefined): number => {
  if (configuredDelayMs === null) return getRandomCommitBatchDelayMs();
  return Math.max(0, configuredDelayMs ?? 0);
};

export const getCommitRowIdBatches = (
  commitRowIds: string[],
  batchSize = IMPORT_COMMIT_BATCH_SIZE,
): string[][] => {
  const safeBatchSize = Math.max(1, batchSize);
  const batches: string[][] = [];
  for (let index = 0; index < commitRowIds.length; index += safeBatchSize) {
    batches.push(commitRowIds.slice(index, index + safeBatchSize));
  }
  return batches;
};

export const buildSimulatedCommitPreviewRows = (
  rowCount = IMPORT_COMMIT_SIMULATION_ROW_COUNT,
): ImportPreviewRow[] => {
  return Array.from({ length: rowCount }, (_unused, index) => ({
    rowId: `simulation-row-${index + 1}`,
    rowNumber: index + 1,
    status: "valid",
    mappedRecord: {
      surname: `Simulation${index + 1}`,
      givenName: "Import",
      type: "SECURITY_PLAN",
      hospital: "Simulation",
      dateListed: "2026-01-01",
      umrn: `SIM-${String(index + 1).padStart(5, "0")}`,
    },
    errors: [],
    matchConfidence: "HIGH",
    reviewPriority: "READY",
    reviewReasons: [],
    matchPreview: {
      matchedOn: "UMRN",
      matchBasis: "UMRN",
      patientId: `simulation-patient-${index + 1}`,
      candidateCount: 1,
    },
  }));
};

export const IMPORT_HEADER_ALIASES: Record<ImportTargetField, string[]> = {
  surname: ["surname", "last name", "family name"],
  givenName: ["givenname", "given name", "first name", "forename"],
  aliases: ["aliases", "alias", "known as", "aka"],
  sex: ["sex", "gender"],
  dob: ["dob", "date of birth"],
  umrn: ["umrn", "mrn", "medical record number"],
  type: ["type", "alert type", "register type"],
  hospital: ["hospital", "source hospital"],
  dateListed: ["datelisted", "date listed", "listed date", "date"],
  comments: ["comments", "comment", "notes"],
};

export type SummaryCounts = {
  total: number;
  ready: number;
  needsReview: number;
  errors: number;
  matchedOnUmrn: number;
  matchedOnDemographics: number;
  newPatients: number;
  updates: number;
  excluded: number;
};

export const createDefaultImportMapping = (columns: Array<{ index: number; label: string }>): ImportMapping => {
  const mapping: ImportMapping = { ...EMPTY_IMPORT_MAPPING };
  columns.forEach((column) => {
    const normalized = column.label.trim().toLowerCase();
    const match = IMPORT_TARGET_FIELDS.find((field) => IMPORT_HEADER_ALIASES[field.field].includes(normalized));
    if (match && mapping[match.field] === null) {
      mapping[match.field] = column.index;
    }
  });
  return mapping;
};

export const getMissingRequiredMappings = (mapping: ImportMapping): ImportTargetField[] => {
  return REQUIRED_IMPORT_TARGET_FIELDS.filter((field) => mapping[field] === null);
};

export const getDuplicateMappedSourceColumns = (mapping: ImportMapping): number[] => {
  const mappedColumns = Object.values(mapping).filter((value): value is number => value !== null);
  return mappedColumns.filter((column, index) => mappedColumns.indexOf(column) !== index);
};

export const getMappingErrors = (mapping: ImportMapping): string[] => {
  const missing = getMissingRequiredMappings(mapping).map((field) => `${field} is required`);
  const duplicates = getDuplicateMappedSourceColumns(mapping).map((column) => `Column ${column + 1} is already mapped`);
  return [...missing, ...duplicates];
};

export const getImportMatchBasis = (row: ImportPreviewRow): ImportMatchBasis | undefined => {
  if (row.matchPreview?.matchBasis) return row.matchPreview.matchBasis;
  if (row.matchPreview?.matchedOn === "UMRN") return "UMRN";
  if (row.matchPreview?.matchedOn === "NAME_DOB_SEX") return "NAME_DOB_SEX";
  if (row.matchPreview?.matchedOn === "CREATED_NEW") return "CREATED_NEW";
  if (row.matchPreview?.matchedOn === "ERROR") return "ERROR";
  return undefined;
};

export function filterPreviewRows(rows: ImportPreviewRow[], filter: ImportReviewFilter): ImportPreviewRow[] {
  switch (filter) {
    case "all":
      return rows;
    case "needsReview":
      return rows.filter((row) => row.reviewPriority === "NEEDS_REVIEW");
    case "ready":
      return rows.filter((row) => row.reviewPriority === "READY");
    case "errors":
      return rows.filter((row) => row.reviewPriority === "ERROR");
    case "matched":
      return rows.filter((row) =>
        row.matchPreview?.matchedOn === "UMRN"
        || row.matchPreview?.matchedOn === "NAME_DOB_SEX"
        || row.reviewReasons.some((reason) => reason.toLowerCase().includes("update")),
      );
    case "createsNew":
      return rows.filter((row) => row.matchPreview?.matchedOn === "CREATED_NEW");
    case "matchUmrn":
      return rows.filter((row) => getImportMatchBasis(row) === "UMRN");
    case "matchNameDobSex":
      return rows.filter((row) => getImportMatchBasis(row) === "NAME_DOB_SEX");
    case "matchNameDob":
      return rows.filter((row) => getImportMatchBasis(row) === "NAME_DOB");
    case "matchNameSex":
      return rows.filter((row) => getImportMatchBasis(row) === "NAME_SEX");
    case "matchNameOnly":
      return rows.filter((row) => getImportMatchBasis(row) === "NAME_ONLY");
    default:
      return rows;
  }
}

export const getReviewFilterCounts = (rows: ImportPreviewRow[]): Record<ImportReviewFilter, number> => {
  return REVIEW_FILTERS.reduce((counts, filter) => ({
    ...counts,
    [filter.value]: filterPreviewRows(rows, filter.value).length,
  }), {} as Record<ImportReviewFilter, number>);
};

export const getTotalPages = (rowCount: number, pageSize = PAGE_SIZE): number => {
  return Math.max(1, Math.ceil(rowCount / pageSize));
};

export const getVisiblePageRows = <TRow>(rows: TRow[], page: number, pageSize = PAGE_SIZE): TRow[] => {
  return rows.slice(page * pageSize, (page + 1) * pageSize);
};

export function buildCommitRequest({
  clientImportId,
  sourceFingerprint,
  csvText,
  hasHeader,
  mapping,
  previewRows,
  approvedRowIds,
  excludedRowIds,
  selectedPatientIds,
}: {
  clientImportId: string;
  sourceFingerprint: string;
  csvText: string;
  hasHeader: boolean;
  mapping: ImportMapping;
  previewRows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
}): ImportCommitRequest {
  const commitRowIds = getCommitRowIds({
    previewRows,
    approvedRowIds,
    excludedRowIds,
  });

  return {
    clientImportId,
    sourceFingerprint,
    csvText,
    hasHeader,
    mapping,
    approvedRowIds: Array.from(approvedRowIds),
    excludedRowIds: Array.from(excludedRowIds),
    commitRowIds,
    selectedPatientIds,
  };
}

export const getCommitRowIds = ({
  previewRows,
  approvedRowIds,
  excludedRowIds,
}: {
  previewRows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
}): string[] => {
  return previewRows
    .filter((row) => row.reviewPriority !== "ERROR" && !excludedRowIds.has(row.rowId))
    .filter((row) => row.reviewPriority === "READY" || approvedRowIds.has(row.rowId))
    .map((row) => row.rowId);
};

const getPreviewPatientName = (row: ImportPreviewRow): string | undefined => {
  const name = `${row.mappedRecord?.givenName ?? ""} ${row.mappedRecord?.surname ?? ""}`.trim();
  return name || undefined;
};

const buildPreviewCommitOutcome = (
  row: ImportPreviewRow,
  status: ImportCommitRowOutcome["status"],
  errorMessage?: string,
): ImportCommitRowOutcome => ({
  rowId: row.rowId,
  rowNumber: row.rowNumber,
  patientName: getPreviewPatientName(row),
  status,
  matchConfidence: row.matchConfidence,
  reviewPriority: row.reviewPriority,
  reviewReasons: row.reviewReasons,
  patientId: row.matchPreview?.patientId,
  errorMessage,
});

const buildSimulatedCommitOutcome = (
  request: ImportCommitRequest,
  row: ImportPreviewRow,
): ImportCommitRowOutcome => {
  const status = row.matchPreview?.matchedOn === "CREATED_NEW"
    ? "CREATED_NEW"
    : row.reviewReasons.some((reason) => {
      const normalized = reason.toLowerCase();
      return normalized.includes("update") || normalized.includes("reactivat");
    })
      ? "UPDATED_EXISTING"
      : row.matchPreview?.matchedOn === "NAME_DOB_SEX"
        ? "MATCHED_DEMOGRAPHICS"
        : "MATCHED_UMRN";

  return {
    rowId: row.rowId,
    rowNumber: row.rowNumber,
    patientName: getPreviewPatientName(row),
    status,
    matchConfidence: row.matchConfidence,
    reviewPriority: row.reviewPriority,
    reviewReasons: row.reviewReasons,
    patientId: request.selectedPatientIds?.[row.rowId] ?? row.matchPreview?.patientId,
  };
};

export const buildSimulatedCommitChunkResponse = ({
  request,
  previewRows,
  commitRowIds,
  batchSize = IMPORT_COMMIT_BATCH_SIZE,
}: {
  request: ImportCommitRequest;
  previewRows: ImportPreviewRow[];
  commitRowIds: string[];
  batchSize?: number;
}): ImportCommitResponse => {
  const rowIds = new Set(commitRowIds);
  const rows = previewRows
    .filter((row) => rowIds.has(row.rowId))
    .map((row) => buildSimulatedCommitOutcome(request, row));

  return {
    clientImportId: request.clientImportId,
    sourceFingerprint: request.sourceFingerprint,
    status: "completed",
    simulated: true,
    batchSize,
    totalBatches: 1,
    currentBatch: 1,
    totalRecords: previewRows.length,
    successfullyImported: rows.length,
    errors: 0,
    skippedOrExcluded: 0,
    retryableRows: 0,
    matchedOnUmrn: rows.filter((row) => row.status === "MATCHED_UMRN").length,
    matchedOnDemographics: rows.filter((row) => row.status === "MATCHED_DEMOGRAPHICS").length,
    newPatientsCreated: rows.filter((row) => row.status === "CREATED_NEW").length,
    updatedExistingEntries: rows.filter((row) => row.status === "UPDATED_EXISTING").length,
    batches: [{
      batchNumber: 1,
      status: "completed",
      attemptedRows: commitRowIds.length,
      committedRows: rows.length,
      failedRows: 0,
      errors: [],
    }],
    rows,
  };
};

export const buildAggregatedCommitResponse = ({
  request,
  previewRows,
  commitRowIdBatches,
  batchResponses,
  activeBatchIndex,
  failedBatchIndex,
  failureMessage,
  simulated = false,
  batchSize = IMPORT_COMMIT_BATCH_SIZE,
}: {
  request: ImportCommitRequest;
  previewRows: ImportPreviewRow[];
  commitRowIdBatches: string[][];
  batchResponses: Array<{ batchIndex: number; response: ImportCommitResponse }>;
  activeBatchIndex?: number;
  failedBatchIndex?: number;
  failureMessage?: string;
  simulated?: boolean;
  batchSize?: number;
}): ImportCommitResponse => {
  const commitRowIds = commitRowIdBatches.flat();
  const commitRowIdSet = new Set(commitRowIds);
  const rowsById = new Map<string, ImportCommitRowOutcome>();

  previewRows.forEach((row) => {
    if (row.reviewPriority === "ERROR") {
      rowsById.set(row.rowId, buildPreviewCommitOutcome(row, "ERROR", row.errors.join("; ") || i18n.t("externalRegister.import.model.validation.rowFailed")));
      return;
    }
    rowsById.set(row.rowId, buildPreviewCommitOutcome(row, "SKIPPED"));
  });

  const completedBatchIndexes = new Set<number>();
  batchResponses.forEach(({ batchIndex, response }) => {
    completedBatchIndexes.add(batchIndex);
    const batchRowIds = new Set(commitRowIdBatches[batchIndex] ?? []);
    response.rows
      .filter((row) => batchRowIds.has(row.rowId))
      .forEach((row) => rowsById.set(row.rowId, row));
  });

  if (failedBatchIndex !== undefined) {
    commitRowIdBatches.slice(failedBatchIndex).flat().forEach((rowId) => {
      const existing = rowsById.get(rowId);
      if (existing && COMMITTED_IMPORT_STATUSES.has(existing.status)) return;
      const previewRow = previewRows.find((row) => row.rowId === rowId);
      if (!previewRow) return;
      rowsById.set(
        rowId,
        buildPreviewCommitOutcome(
          previewRow,
          "ERROR",
          failureMessage || i18n.t("externalRegister.import.model.validation.notCommitted"),
        ),
      );
    });
  }

  const rows = previewRows.map((row) => rowsById.get(row.rowId) ?? buildPreviewCommitOutcome(row, "SKIPPED"));
  const attemptedRowIds = new Set(
    Array.from(completedBatchIndexes)
      .flatMap((batchIndex) => commitRowIdBatches[batchIndex] ?? []),
  );
  const currentBatch = failedBatchIndex !== undefined
    ? Math.min(failedBatchIndex + 1, commitRowIdBatches.length)
    : activeBatchIndex !== undefined
      ? Math.min(activeBatchIndex + 1, commitRowIdBatches.length)
    : Math.min(completedBatchIndexes.size, commitRowIdBatches.length);
  const batches: ImportCommitBatchOutcome[] = commitRowIdBatches.map((batchRowIds, index) => {
    const batchRowIdSet = new Set(batchRowIds);
    const batchRows = rows.filter((row) => batchRowIdSet.has(row.rowId));
    const committedRows = batchRows.filter((row) => COMMITTED_IMPORT_STATUSES.has(row.status)).length;
    const failedRows = batchRows.filter((row) => row.status === "ERROR").length;
    const responseErrors = batchResponses
      .find((batchResponse) => batchResponse.batchIndex === index)
      ?.response.batches
      .flatMap((batch) => batch.errors)
      .filter((error) => !error.rowId || batchRowIdSet.has(error.rowId)) ?? [];
    const errors = index === failedBatchIndex && failureMessage
      ? [{ message: failureMessage }, ...responseErrors]
      : responseErrors;
    const status: ImportCommitBatchOutcome["status"] = index === failedBatchIndex
      ? "failed"
      : index === activeBatchIndex && !completedBatchIndexes.has(index)
        ? "committing"
      : completedBatchIndexes.has(index)
        ? failedRows > 0
          ? committedRows > 0
            ? "completed_with_errors"
            : "failed"
          : "completed"
        : "pending";

    return {
      batchNumber: index + 1,
      status,
      attemptedRows: batchRowIds.length,
      committedRows,
      failedRows,
      errors,
    };
  });

  const retryableRows = rows.filter((row) => commitRowIdSet.has(row.rowId) && row.status === "ERROR").length;
  const errors = rows.filter((row) => row.status === "ERROR").length;
  const skippedOrExcluded = rows.filter((row) =>
    row.status === "SKIPPED"
    && (!commitRowIdSet.has(row.rowId) || attemptedRowIds.has(row.rowId)),
  ).length;
  const status: ImportCommitResponse["status"] = failedBatchIndex !== undefined
    ? "failed"
    : activeBatchIndex !== undefined || currentBatch < commitRowIdBatches.length
      ? "committing"
      : errors > 0
        ? "completed_with_errors"
        : "completed";

  return {
    clientImportId: request.clientImportId,
    sourceFingerprint: request.sourceFingerprint,
    status,
    simulated,
    batchSize,
    totalBatches: commitRowIdBatches.length,
    currentBatch,
    totalRecords: previewRows.length,
    successfullyImported: rows.filter((row) => COMMITTED_IMPORT_STATUSES.has(row.status)).length,
    errors,
    skippedOrExcluded,
    retryableRows,
    matchedOnUmrn: rows.filter((row) => row.status === "MATCHED_UMRN").length,
    matchedOnDemographics: rows.filter((row) => row.status === "MATCHED_DEMOGRAPHICS").length,
    newPatientsCreated: rows.filter((row) => row.status === "CREATED_NEW").length,
    updatedExistingEntries: rows.filter((row) => row.status === "UPDATED_EXISTING").length,
    batches,
    rows,
  };
};

export const getOpenReviewRows = ({
  previewRows,
  approvedRowIds,
  excludedRowIds,
}: {
  previewRows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
}): ImportPreviewRow[] => {
  return previewRows.filter((row) =>
    row.reviewPriority === "NEEDS_REVIEW"
    && !approvedRowIds.has(row.rowId)
    && !excludedRowIds.has(row.rowId),
  );
};

export const canStartImport = ({
  previewRows,
  approvedRowIds,
  excludedRowIds,
  selectedPatientIds,
}: {
  previewRows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
}): boolean => {
  return getCommitRowIds({ previewRows, approvedRowIds, excludedRowIds }).length > 0
    && getOpenReviewRows({ previewRows, approvedRowIds, excludedRowIds }).length === 0
    && getRowsMissingRequiredCandidateSelection({ rows: previewRows, approvedRowIds, selectedPatientIds }).length === 0;
};

export function getSummaryCounts(rows: ImportPreviewRow[], excludedRowIds: Set<string>): SummaryCounts {
  return {
    total: rows.length,
    ready: rows.filter((row) => row.reviewPriority === "READY").length,
    needsReview: rows.filter((row) => row.reviewPriority === "NEEDS_REVIEW").length,
    errors: rows.filter((row) => row.reviewPriority === "ERROR").length,
    matchedOnUmrn: rows.filter((row) => row.matchPreview?.matchedOn === "UMRN").length,
    matchedOnDemographics: rows.filter((row) => row.matchPreview?.matchedOn === "NAME_DOB_SEX").length,
    newPatients: rows.filter((row) => row.matchPreview?.matchedOn === "CREATED_NEW").length,
    updates: rows.filter((row) => row.reviewReasons.some((reason) => reason.toLowerCase().includes("update"))).length,
    excluded: excludedRowIds.size,
  };
}

export function hasActiveDraft(
  csvText: string,
  parseResponse: ImportParseResponse | null,
  validateResponse: ImportValidateResponse | null,
): boolean {
  return Boolean(csvText.trim() || parseResponse || validateResponse);
}

export function isMultipleCandidateRow(row: ImportPreviewRow): boolean {
  return (row.matchPreview?.candidateCount ?? 0) > 1;
}

export const isCandidateSelectionMissing = (
  row: ImportPreviewRow,
  selectedPatientId?: string,
): boolean => {
  return isMultipleCandidateRow(row) && !selectedPatientId;
};

export const canApproveReviewRow = (
  row: ImportPreviewRow,
  selectedPatientId?: string,
): boolean => {
  return row.reviewPriority === "NEEDS_REVIEW" && !isCandidateSelectionMissing(row, selectedPatientId);
};

export const isMatchedReviewRow = (row: ImportPreviewRow): boolean => {
  return row.matchPreview?.matchedOn === "UMRN" || row.matchPreview?.matchedOn === "NAME_DOB_SEX";
};

export const isAtOrAboveConfidenceThreshold = (
  confidence: ImportMatchConfidence,
  threshold: ImportApprovalThreshold,
): boolean => {
  return CONFIDENCE_RANK[confidence] >= CONFIDENCE_RANK[threshold];
};

export const isMatchingApprovalBasis = (
  row: ImportPreviewRow,
  basis: ImportApprovalBasis = "allMatched",
): boolean => {
  if (basis === "allMatched") return isMatchedReviewRow(row);
  return getImportMatchBasis(row) === basis;
};

export const getBulkApprovableRows = ({
  rows,
  threshold,
  basis = "allMatched",
  approvedRowIds,
  excludedRowIds,
  selectedPatientIds,
}: {
  rows: ImportPreviewRow[];
  threshold: ImportApprovalThreshold;
  basis?: ImportApprovalBasis;
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
}): ImportPreviewRow[] => {
  return rows.filter((row) =>
    !approvedRowIds.has(row.rowId)
    && !excludedRowIds.has(row.rowId)
    && isAtOrAboveConfidenceThreshold(row.matchConfidence, threshold)
    && isMatchingApprovalBasis(row, basis)
    && canApproveReviewRow(row, selectedPatientIds[row.rowId]),
  );
};

export const getBulkBlockedCandidateRows = ({
  rows,
  threshold,
  basis = "allMatched",
  approvedRowIds,
  excludedRowIds,
  selectedPatientIds,
}: {
  rows: ImportPreviewRow[];
  threshold: ImportApprovalThreshold;
  basis?: ImportApprovalBasis;
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
}): ImportPreviewRow[] => {
  return rows.filter((row) =>
    !approvedRowIds.has(row.rowId)
    && !excludedRowIds.has(row.rowId)
    && isAtOrAboveConfidenceThreshold(row.matchConfidence, threshold)
    && isMatchingApprovalBasis(row, basis)
    && isCandidateSelectionMissing(row, selectedPatientIds[row.rowId]),
  );
};

export const getRowsMissingRequiredCandidateSelection = ({
  rows,
  approvedRowIds,
  selectedPatientIds,
}: {
  rows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
}): ImportPreviewRow[] => {
  return rows.filter((row) =>
    isMultipleCandidateRow(row)
    && approvedRowIds.has(row.rowId)
    && !selectedPatientIds[row.rowId],
  );
};

export function isHighRiskRow(row: ImportPreviewRow): boolean {
  return row.reviewPriority === "ERROR" || row.reviewReasons.length > 0 || isMultipleCandidateRow(row);
}

export const mergeReviewableCommitErrors = (
  rows: ImportPreviewRow[],
  commitResponse: ImportCommitResponse,
): ImportPreviewRow[] => {
  const reviewableErrors = new Map(commitResponse.rows
    .filter((row) => row.status === "ERROR")
    .map((row) => [row.rowId, row.errorMessage ?? i18n.t("externalRegister.import.model.validation.commitRejected")]));

  if (reviewableErrors.size === 0) return rows;

  return rows.map((row) => {
    const commitError = reviewableErrors.get(row.rowId);
    if (!commitError) return row;

    return {
      ...row,
      reviewPriority: "NEEDS_REVIEW",
      commitError,
      errors: row.errors.filter((error) => error !== commitError),
    };
  });
};

export function getStageStatus({
  stage,
  csvText,
  parseResponse,
  mappingErrors,
  validateResponse,
  latestCommitResponse,
}: {
  stage: ImportStage;
  csvText: string;
  parseResponse: ImportParseResponse | null;
  mappingErrors: string[];
  validateResponse: ImportValidateResponse | null;
  latestCommitResponse: ImportCommitResponse | null;
}): ImportStageStatus {
  switch (stage) {
    case "source":
      if (parseResponse) return "complete";
      return csvText.trim() ? "inProgress" : "waiting";
    case "parse":
      if (parseResponse) return "complete";
      return csvText.trim() ? "inProgress" : "waiting";
    case "mapping":
      if (validateResponse) return "complete";
      if (parseResponse && mappingErrors.length === 0) return "inProgress";
      return "waiting";
    case "review":
      if (latestCommitResponse) return "complete";
      return validateResponse ? "inProgress" : "waiting";
    case "report":
      return latestCommitResponse ? "complete" : "waiting";
    default:
      return "waiting";
  }
}
